import { flow, types, Instance } from 'mobx-state-tree';
import { replaceVariables } from '../utils';
import Communicators from './Communicators';
import { Campaign, Influencer, View } from './models';

/**
 * Global state needed:
 * - Campaign settings (API)
 *    - Offer text/description
 * - config (from script)
 *    - debug mode
 *    - display modes/behviors
 *    - theme overrides
 *    * lifecycle hooks
 * - current display state
 *    - opened, expanded, minimized, closed
 */

const Root = types
	.model({
		Communicators,
		View,
		Campaign: types.maybe(Campaign),
		Influencer: types.maybe(Influencer),
		inviteId: types.maybeNull(types.string),
		transactionId: types.maybeNull(types.string),
		productURL: types.maybe(types.string),
		productName: types.maybe(types.string, 'Exclusive offer'),
		email: types.maybe(types.string),
		engagement: types.maybeNull(types.string),
		promocode: types.maybe(types.string),
		publicKey: types.maybe(types.string),
		revenue: types.maybe(types.string), // probably want to do some checking for correct revenue
		flavor: 'undefined',
		shared: false,
	})
	.named('CampaignReferralRootStore')
	// api actions
	.actions((self) => ({
		getCampaign(campaignId) {
			const api = self.Communicators.Client.getCampaignInformation;
			return self.Communicators.createRequest(api)
				.withArgs({
					campaignId,
				})
				.withParams({ publicKey: self.publicKey })
				.withOptions({
					transformResponse: ({ data }) => data,
					transformErrorResponse: ({ data, status }) => ({ ...data, status }),
				})
				.call();
		},
		postEmail(campaignId, body) {
			const api = self.Communicators.Client.sendEmailToInfluencer;
			return self.Communicators.createRequest(api)
				.withParams({ publicKey: self.publicKey })
				.withArgs({
					campaignId,
					...body,
				})
				.withOptions({
					transformResponse: ({ data }) => data,
					transformErrorResponse: ({ data, status }) => ({ ...data, status }),
				})
				.call();
		},
		postInfluencer(campaignId, body) {
			const api = self.Communicators.V1.createInfluencer2;
			return self.Communicators.createRequest(api)
				.withParams({ publicKey: self.publicKey })
				.withBaseUrl(`${process.env.BASE_API_ENDPOINT}/api/v1`)
				.withArgs({
					campaignId,
					...body,
				})
				.withOptions({
					transformResponse: ({ data }) => data,
					transformErrorResponse: ({ data, status }) => ({ ...data, status }),
				})
				.call();
		},
		postConversion(campaignId, body) {
			// why is it named like this???
			const api =
				self.Communicators.Client
					.createInfluencerFromClientIntermediaryConversion;
			return self.Communicators.createRequest(api)
				.withParams({ publicKey: self.publicKey })
				.withArgs({
					campaignId,
					...body,
				})
				.withOptions({
					transformResponse: ({ data }) => data,
					transformErrorResponse: ({ data, status }) => ({ ...data, status }),
				})
				.call();
		},
	}))
	// flows
	.actions((self) => ({
		initCampaign: flow(function*(campaignId) {
			try {
				self.View.setIsLoading('campaign');
				const { data, message, success } = yield self.getCampaign(campaignId);
				// short circuit on failure
				if (!success) throw message;

				const { campaign } = data;
				self.Campaign = campaign;
			} catch (e) {
				console.error(e);
			} finally {
				self.View.setIsLoading('campaign', false);
			}
		}),
		createInfluencer: flow(function*(campaignId, body) {
			try {
				self.View.setIsLoading('influencer');
				// handle undefined/null orderIds in case of pre-purchase
				const reqBody = {
					...body,
					inviteId: self.inviteId,
					transactionId: self.transactionId,
					content: self.flavor,
					orderId: body.orderId ?? 'None',
				};
				const { influencer } = yield self.postInfluencer(campaignId, reqBody);

				if (!influencer) {
					throw 'Error creating influencer';
				}

				self.Influencer = influencer;
			} catch (e) {
				console.error(e);
				throw e;
			} finally {
				self.View.setIsLoading('influencer', false);
			}
		}),
		sendEmail: flow(function*(
			message = '',
			recipients = [],
			onSent = () => null
		) {
			try {
				if (!self.Campaign?.id) throw 'Invalid Campaign';
				if (!self.Influencer?.id) throw 'No Influencer';

				const {
					emailSubject: subject,
					id,
					fromEmail,
					shareImage,
				} = self.Campaign;
				const { name: sender, inviteLink: shareLink } = self.Influencer;

				const invites = recipients.map((toEmail) => ({
					toEmail,
					sender,
					shareLink,
				}));

				const { data, success } = yield self.postEmail(id, {
					fromEmail,
					message,
					subject,
					invites,
					imageUrl: `${shareImage}`,
					title: self.shareTitle,
					shareMessage: self.shareMessage,
				});

				self.setShared(success);
				return { success, data };
			} catch (e) {
				console.error(e);
			}
		}),
		trackConversion: flow(
			/**
			 * Tags a conversion
			 *
			 * @param {object} conversionParams
			 * @param {string} conversionParams.email
			 * @param {'transaction' | 'signup' | 'participation' | 'pageview'} conversionParams.conversionType
			 * @param {Instance<typeof Influencer>} [conversionParams.influencer = self.Influencer]
			 * @param {string} conversionParams.campaignId
			 */
			function*({ influencer, email, conversionType, campaignId, revenue }) {
				try {
					self.View.setIsLoading('influencer');
					const { orderId } = influencer || self.Influencer;
					// handle undefined/null orderIds in case of pre-purchase
					const reqBody = {
						revenue: revenue || self.Influencer.revenue,
						transactionId: self.transactionId,
						inviteId: self.inviteId ?? undefined,
						orderId: orderId ?? 'None',
						identifier: email,
						name: email,
						email,
						conversionType,
					};

					yield self.postConversion(campaignId, reqBody);
				} catch (e) {
					console.error(e);
				} finally {
					self.View.setIsLoading('influencer', false);
				}
			}
		),
		setFlavor(flavor) {
			self.flavor = flavor;
		},
		setEmail(email) {
			self.Influencer.setEmail(email);
		},
		setEngagement(engagement) {
			self.engagement = engagement;
		},
		setInviteId(inviteId) {
			self.inviteId = inviteId;
		},
		setOrderId(orderId) {
			self.Influencer.setOrderId(orderId);
		},
		setInfluencer(influencer) {
			self.Influencer.setInfluencer(influencer);
		},
		setName(name) {
			self.Influencer.setName(name);
		},
		setRevenue(revenue) {
			self.Influencer.setRevenue(revenue);
		},
		/**
		 * @deprecated
		 */
		setOrigin() {
			return;
		},
		setPromocode(promocode) {
			self.promocode = promocode;
		},
		setProductName(productName) {
			self.productName = productName;
		},
		setProductURL(productURL) {
			self.productURL = productURL;
		},
		setPublicKey(publicKey) {
			self.publicKey = publicKey;
		},
		setShared(shared) {
			self.shared = shared;
		},
		setTransactionId(transactionId) {
			self.transactionId = transactionId || null;
		},
	}))
	.views((self) => ({
		replaceVariables(toReplace) {
			const varMap = {
				'influencer.name':
					self.Influencer?.name || self.Influencer?.email || 'Guest',
				'product.name': self.productName,
				'product.url': self.productURL,
				promocode: self.promocode,
			};
			return replaceVariables(toReplace, varMap);
		},
		get offerText() {
			return self.replaceVariables(self.Campaign?.offerInstructions);
		},
		get offerHeading() {
			return self.replaceVariables(self.Campaign?.offerHeading);
		},
		get shareMessage() {
			return self.replaceVariables(self.Campaign?.shareMessage);
		},
		get shareTitle() {
			return self.replaceVariables(self.Campaign?.shareTitle);
		},
	}));

export default types.snapshotProcessor(Root, {
	preProcessor({ Influencer, ...snapshot }) {
		return {
			...snapshot,
			Influencer: Influencer || {},
		};
	},
});
